/**
 * Shared Google Maps SDK loader + tiny utilities.
 *
 * - APIProvider is mounted by each map component independently. The
 *   @vis.gl/react-google-maps package internally de-duplicates loads, so even
 *   if both <LocationPreview> and <LocationIntelligence> mount on the same
 *   page only ONE <script> tag is created.
 * - Lazy by component placement: importing this module does nothing until a
 *   component renders, satisfying "load only on pages with map components".
 */
export const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
export const GMAPS_LIBS = ["places", "geocoding"];
// mapId is required for AdvancedMarker. Google's "DEMO_MAP_ID" works for dev;
// switch to a styled map id from Google Cloud Console when ready.
export const GMAPS_MAP_ID = "DEMO_MAP_ID";

export const isMapsKeyConfigured = () => !!GMAPS_KEY;

/** Promisified Geocoder.geocode for {address} -> {lat, lng, formatted, county}. */
export async function geocodeAddress({ address, city, state, zip_code }) {
  if (!window.google?.maps?.Geocoder) {
    throw new Error("Google Maps SDK not loaded yet");
  }
  const parts = [address, city, state, zip_code].filter(Boolean).map((s) => String(s).trim());
  const geocoder = new window.google.maps.Geocoder();
  const { results } = await geocoder.geocode({ address: parts.join(", ") });
  if (!results?.length) throw new Error("ZERO_RESULTS");
  const r0 = results[0];
  const loc = r0.geometry.location;
  // In the US, address_components includes administrative_area_level_2 which
  // is the county. Strip the trailing " County" suffix so it matches the
  // free-form text the operator would type.
  const countyComp = (r0.address_components || []).find((c) =>
    (c.types || []).includes("administrative_area_level_2"),
  );
  const county = countyComp?.long_name?.replace(/\s+County$/i, "") || "";
  return {
    lat: loc.lat(),
    lng: loc.lng(),
    formatted_address: r0.formatted_address,
    place_id: r0.place_id,
    county,
  };
}

/** Categorized PlacesService.nearbySearch for the Opportunity detail page. */
const NEARBY_CATEGORIES = [
  { key: "schools",     label: "Schools",     type: "school",          radius: 1600, limit: 3 },
  { key: "grocery",     label: "Grocery",     type: "supermarket",     radius: 2400, limit: 2 },
  { key: "hospitals",   label: "Hospitals",   type: "hospital",        radius: 4800, limit: 1 },
  { key: "transit",     label: "Transit",     type: "transit_station", radius: 1600, limit: 3 },
  { key: "restaurants", label: "Restaurants", type: "restaurant",      radius: 1600, limit: 3 },
];

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.7613, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function nearbyCategory({ lat, lng, cat }) {
  // Use the new Place.searchNearby API (legacy PlacesService is not enabled for
  // projects created after March 2025). Returns Promise<{...cat, items, count}>.
  return (async () => {
    try {
      if (!window.google?.maps?.importLibrary) {
        return { ...cat, items: [], count: 0 };
      }
      const { Place } = await window.google.maps.importLibrary("places");
      const request = {
        fields: ["id", "displayName", "location", "formattedAddress", "rating"],
        locationRestriction: {
          center: { lat, lng },
          radius: cat.radius,
        },
        includedPrimaryTypes: [cat.type],
        maxResultCount: Math.min(cat.limit * 3, 20),
        rankPreference: "DISTANCE",
      };
      const { places } = await Place.searchNearby(request);
      const items = (places || []).map((p) => {
        const loc = p.location;
        const plat = typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
        const plng = typeof loc?.lng === "function" ? loc.lng() : loc?.lng;
        return {
          place_id: p.id,
          name: p.displayName || "",
          address: p.formattedAddress || "",
          lat: plat,
          lng: plng,
          rating: p.rating,
          distance_miles: Number(haversineMiles(lat, lng, plat || 0, plng || 0).toFixed(2)),
        };
      }).filter((i) => Number.isFinite(i.lat) && Number.isFinite(i.lng));
      items.sort((a, b) => a.distance_miles - b.distance_miles);
      const top = items.slice(0, cat.limit);
      return { ...cat, items: top, count: top.length };
    } catch {
      return { ...cat, items: [], count: 0 };
    }
  })();
}

/** Fetch all 5 categories in parallel. Uses the new Places API (Place.searchNearby). */
export async function fetchNearbyAmenities({ lat, lng }) {
  const results = await Promise.all(
    NEARBY_CATEGORIES.map((cat) => nearbyCategory({ lat, lng, cat })),
  );
  return Object.fromEntries(results.map((r) => [r.key, r]));
}
